import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {SureFlowUnitData} from '../../model/sure-flow-unit-data';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UnitSelectionService} from '../../services/unit-selection-service';
import {SelectionSystemType} from '../../model/selection-system-type';
import {StringUtils} from '../../utils/string-utils';
import {WorkingSureFlowUnit} from '../../model/working-sure-flow-unit';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {SureFlowRatingUnit} from '../../model/sure-flow-rating-unit';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import {MatInput} from '@angular/material/input';
import {MatFormField, MatLabel} from '@angular/material/form-field';


@Component({
    selector: 'app-bulk-edit-sure-flow-units-dialog',
    templateUrl: './bulk-edit-sure-flow-units-dialog.component.html',
    styleUrls: ['./bulk-edit-sure-flow-units-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatLabel, MatInput, FormsModule, MatButton]
})
export class BulkEditSureFlowUnitsDialogComponent implements OnInit {
  private readonly dialogRef = inject<MatDialogRef<BulkEditSureFlowUnitsDialogComponent>>(MatDialogRef);
  private readonly unitSelectionService = inject(UnitSelectionService);
  private readonly data = inject(MAT_DIALOG_DATA);


  systemTypeCode: string;
  unitData: SureFlowUnitData;
  ratingUnit: SureFlowRatingUnit;
  metricInput: boolean;
  isBusy!: boolean;
  readonly errorMessage = signal<string>(undefined!);
  readonly coolingEnteringDryBulb = signal<number>(undefined!);
  readonly coolingEnteringWetBulb = signal<number>(undefined!);
  readonly heatingEnteringDryBulb = signal<number>(undefined!);
  private readonly systemType = signal<SelectionSystemType>(undefined!);

  constructor() {
    const data = this.data;

    this.systemTypeCode = data.systemTypeCode;
    this.unitData = data.unitData;
    this.ratingUnit = data.ratingUnit;
    this.metricInput = data.metricInput || false;
  }

  ngOnInit(): void {
    this.isBusy = true;
    this.unitSelectionService.getSureFlowSystemTypes().subscribe({
      next: (systemTypes) => {
        this.systemType.set(systemTypes.filter(st => st.code === this.systemTypeCode)[0]);
        if (this.ratingUnit) {
          if (this.isCooling()) {
            this.coolingEnteringDryBulb.set(this.ratingUnit.request.primaryCoilRequest.enteringDryBulb);
            this.coolingEnteringWetBulb.set(this.ratingUnit.request.primaryCoilRequest.enteringWetBulb);
            if (this.isHeating()) {
              this.heatingEnteringDryBulb.set(this.ratingUnit.request.secondaryCoilRequest.enteringDryBulb);
            }
          } else if (this.isHeating()) {
            this.heatingEnteringDryBulb.set(this.ratingUnit.request.primaryCoilRequest.enteringDryBulb);
          }
        }
        this.isBusy = false;
      }, error: (error) => this.handleError(error)
    });
  }

  private handleError(error: any) {
    this.isBusy = false;
    this.errorMessage.set(StringUtils.formatErrorMessage(error));
  }

  isCooling(): boolean {
    return StringUtils.equalsIgnoreCase('C', this.systemType()?.primaryCoilApplication);
  }

  isHeating(): boolean {
    return StringUtils.equalsIgnoreCase('H', this.systemType()?.primaryCoilApplication)
      || StringUtils.equalsIgnoreCase('H', this.systemType()?.secondaryCoilApplication);
  }

  save(): void {
    if (this.canSave()) {
      const workingUnit = new WorkingSureFlowUnit();
      workingUnit.unitData = this.unitData;
      workingUnit.unitData.sureFlowRatingUnit = this.ratingUnit;
      if (this.isCooling()) {
        workingUnit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb = this.coolingEnteringDryBulb();
        workingUnit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringWetBulb = this.coolingEnteringWetBulb();
        if (this.isHeating()) {
          workingUnit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.enteringDryBulb = this.heatingEnteringDryBulb();
        }
      } else if (this.isHeating()) {
        workingUnit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb = this.heatingEnteringDryBulb();
      }
      this.dialogRef.close(workingUnit);
    }
  }

  canSave(): boolean {
    if (this.isCooling()) {
      if (StringUtils.isEmptyNumericValue(this.unitData.coolingTargetTotalLoad)) {
        return false;
      }
    }
    if (this.isHeating()) {
      if (StringUtils.isEmptyNumericValue(this.unitData.heatingTargetTotalLoad)) {
        return false;
      }
    }
    return true;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  getCapacityUnits(): string {
    return UnitConversionUtils.getCapacityUnits(this.metricInput);
  }

  getFlowRateUnits(): string {
    return UnitConversionUtils.getFlowRateUnits(this.metricInput);
  }

  getTemperatureUnits(): string {
    return UnitConversionUtils.getTemperatureUnits(this.metricInput);
  }

}
